import React from 'react';

type Block = { type: 'p'; lines: string[] } | { type: 'ul' | 'ol'; items: string[] };

const BULLET = /^\s*[-*•]\s+(.*)$/;
const ORDERED = /^\s*\d+[.)]\s+(.*)$/;

function parseBlocks(text: string): Block[] {
    const blocks: Block[] = [];
    let current: Block | undefined;
    for (const line of text.split('\n')) {
        const bullet = BULLET.exec(line);
        const ordered = bullet ? null : ORDERED.exec(line);
        const listMatch = bullet ?? ordered;
        if (listMatch) {
            const type = bullet ? 'ul' : 'ol';
            if (current?.type !== type) {
                current = { type, items: [] };
                blocks.push(current);
            }
            (current as { items: string[] }).items.push(listMatch[1]);
        } else if (!line.trim()) {
            current = undefined;
        } else {
            if (current?.type !== 'p') {
                current = { type: 'p', lines: [] };
                blocks.push(current);
            }
            current.lines.push(line);
        }
    }
    return blocks;
}

function renderInline(text: string): React.ReactNode[] {
    return text
        .split(/(\*\*[^*]+\*\*|`[^`]+`)/g)
        .filter(Boolean)
        .map((part, i) => {
            if (part.length > 4 && part.startsWith('**') && part.endsWith('**')) {
                return <strong key={i}>{part.slice(2, -2)}</strong>;
            }
            if (part.length > 2 && part.startsWith('`') && part.endsWith('`')) {
                return (
                    <code key={i} className="ai-chat-code">
                        {part.slice(1, -1)}
                    </code>
                );
            }
            return part;
        });
}

export function AiMarkdown(props: { text: string }) {
    const blocks = React.useMemo(() => parseBlocks(props.text), [props.text]);
    return (
        <div className="ai-chat-markdown">
            {blocks.map((block, i) => {
                if (block.type === 'p') {
                    return <p key={i}>{renderInline(block.lines.join('\n'))}</p>;
                }
                const items = block.items.map((item, j) => <li key={j}>{renderInline(item)}</li>);
                return block.type === 'ul' ? <ul key={i}>{items}</ul> : <ol key={i}>{items}</ol>;
            })}
        </div>
    );
}
